import React, { useState } from "react";
import { ButtonProps } from "@mui/material/Button";
import { ButtonBase, SxProps, Theme, useTheme } from "@mui/material";

interface AsyncButtonProps extends ButtonProps {
    onClick?: (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => void | Promise<any>;
    sx?: SxProps<Theme>;
    isPrimary?: boolean;
}

const AsyncButton: React.FC<AsyncButtonProps> = ({
    onClick,
    children,
    sx = {},
    isPrimary = false,
    ...props
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const theme = useTheme();
    const semanticColor =
        props.color === "primary" ||
        props.color === "secondary" ||
        props.color === "error" ||
        props.color === "info" ||
        props.color === "success" ||
        props.color === "warning"
            ? props.color
            : null;

    const handleClick = async (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        setIsLoading(true);
        try {
            if (onClick) {
                const result = onClick(e);
                if (result instanceof Promise) {
                    await result;
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ButtonBase
            onClick={(e) => {
                handleClick(e);
            }}
            sx={{
                fontFamily: "inter-variable",
                fontVariationSettings: "'wght' 700",
                borderRadius: "11px",
                backgroundColor: isPrimary
                    ? "var(--accent)"
                    : semanticColor
                      ? theme.palette[semanticColor].main
                      : "var(--layer-secondary)",
                color: isPrimary
                    ? "white"
                    : semanticColor
                      ? theme.palette[semanticColor].contrastText
                      : "var(--text-primary)",
                padding: "11px 20px 13px 20px",
                fontSize: "14px",
                opacity: isLoading || props.disabled ? 0.6 : 1,
                boxShadow: props.disabled
                    ? "0 3px 3px 0 var(--shadow-base) inset, 0 0 2px 0 var(--shadow-antithesis) inset, 0 1px 1px 0 var(--shadow-decoration) inset, 0 -5px 20px 0 var(--shadow-fume) inset"
                    : "0 4px 20px 0 var(--shadow-base), 0 4px 3px 0 var(--shadow-base), 0 0 3px 0 var(--shadow-antithesis) inset, 0 -7px 20px 0 var(--shadow-fume) inset",
                ...sx,
            }}
            disabled={isLoading || props.disabled}
            {...props}
        >
            {isLoading ? "Loading..." : children}
        </ButtonBase>
    );
};

export default AsyncButton;
